import { useState } from "react";
import { Outlet, Navigate } from "react-router-dom";
import Sidebar, { useSidebarCollapsed } from "@/components/Sidebar";
import { useAuth } from "@/contexts/AuthContext";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import { Menu } from "lucide-react";

export default function AppLayout() {
  const { user, organization, loading } = useAuth();
  const [collapsed, setCollapsed] = useSidebarCollapsed();
  const [mobileOpen, setMobileOpen] = useState(false);

  if (loading) {
    return (
      <div className="min-h-screen grid place-items-center bg-slate-50">
        <div className="text-slate-400 text-sm">Loading…</div>
      </div>
    );
  }

  if (!user) return <Navigate to="/login" replace />;
  if (!organization) return <Navigate to="/onboarding" replace />;

  return (
    <div className="flex min-h-screen bg-slate-50">
      {/* Desktop sidebar */}
      <div className="hidden md:block sticky top-0 self-start z-30">
        <Sidebar collapsed={collapsed} onToggle={() => setCollapsed((v) => !v)} />
      </div>

      {/* Mobile slide-out drawer */}
      <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
        <SheetContent side="left" className="p-0 w-64" data-testid="mobile-sidebar-sheet">
          <VisuallyHidden asChild>
            <SheetTitle>Navigation menu</SheetTitle>
          </VisuallyHidden>
          <Sidebar mobile onNavigate={() => setMobileOpen(false)} />
        </SheetContent>
      </Sheet>

      <main className="flex-1 overflow-x-hidden min-w-0">
        {/* Mobile top bar with hamburger */}
        <div className="md:hidden sticky top-0 z-20 bg-white border-b border-slate-200 px-3 py-2 flex items-center gap-2">
          <SheetTriggerHack onClick={() => setMobileOpen(true)} />
          <span className="text-sm font-semibold text-slate-700">FlowSend</span>
        </div>
        <Outlet />
      </main>
    </div>
  );
}

// Tiny wrapper to avoid nesting <SheetTrigger> outside the <Sheet>.
function SheetTriggerHack({ onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid="mobile-sidebar-open"
      aria-label="Open menu"
      className="p-1.5 rounded-md hover:bg-slate-100 text-slate-600"
    >
      <Menu className="w-5 h-5" />
    </button>
  );
}
